import { DictionaryItem } from "@sa/rep-mdm";
import { isEqual } from "lodash";
import { produce } from "immer";
import type { ICalendarItemDTO } from "../../../../../../api";
import type { BulkCalendarControlDateRange, IBulkCalendarControlAction, IBulkCalendarControlState } from "./BulkCalendarControl.types";
import { virtualFrequency } from "../../../../BulkExportStepper.constants";
import { BulkExportTab } from "../../../../../../providers/BulkExportProvider/BulkExportProvider.types";

function getItemId(item?: DictionaryItem) {
  return item ? Number(item.id) : undefined;
}

export function reducer<T extends string | number | unknown>(state: IBulkCalendarControlState<T>, action: IBulkCalendarControlAction) {
  const { type: actionType, payload } = action;
  switch (actionType) {
    case "initItems": {
      const {
        items,
        lastCalendarItems,
        availableDateRanges,
        tab,
        currentSetting,
        dataSourceId,
        selection = [],
        isSubscribtion,
        isFavourite,
      } = payload.value as any;

      const dateRange: BulkCalendarControlDateRange = selection.length > 0 ? Number(`${selection}`[0]) : availableDateRanges[0];

      const controlItems = items as ICalendarItemDTO<string | number>[];
      const controlLastCalendarItems = lastCalendarItems as DictionaryItem[];
      const hasChangesAvailableDateRanges = !isEqual(availableDateRanges, state.availableDateRanges);
      const hasChangesDataSourceId = tab
        ? !isEqual(currentSetting?.parentId, state.dataSourceId)
        : !isEqual(dataSourceId, state.dataSourceId);
      const hasChangedCurrentSavedSettings = !isEqual(currentSetting?.id, state.currentSavedSettingsId);
      const hasChangedTab = tab && !isEqual(state.tab, tab);
      const hasChangedLastCalendarItems = tab
        ? !isEqual(
            controlLastCalendarItems.find((it) => it.getSingleValue("frequencyId") === dateRange),
            state.lastCalendar[dateRange]
          )
        : availableDateRanges?.some((dateRange) => {
            let filtredItems = controlItems.filter((it) => it.frequencyId === dateRange);

            const lastCalendarItem = controlLastCalendarItems.find((it) => it.getSingleValue("frequencyId") === dateRange);
            const lastItem = filtredItems[filtredItems.length - 1];
            const lastCalendarItemId = getItemId(lastCalendarItem) ?? lastItem?.id;
            return state.lastCalendar[dateRange] !== lastCalendarItemId;
          });
      if (
        !hasChangesAvailableDateRanges &&
        !hasChangedCurrentSavedSettings &&
        !hasChangesDataSourceId &&
        !hasChangedLastCalendarItems &&
        !hasChangedTab
      ) {
        return state;
      }

      const currentYear = new Date().getFullYear().toString();
      return produce(state, (draft) => {
        draft.availableDateRanges = availableDateRanges ?? [];
        draft.currentSavedSettingsId = currentSetting?.id;
        draft.selectedRanges = availableDateRanges?.length ? [availableDateRanges[0]] : [];
        draft.dataSourceId = tab ? currentSetting?.parentId : dataSourceId;
        draft.tab = tab;
        draft.availableDateRanges.forEach((availableDateRange) => {
          let filtredItems = controlItems.filter((it) => it.frequencyId === availableDateRange);
          const lastCalendarItem = controlLastCalendarItems.find((it) => it.getSingleValue("frequencyId") === availableDateRange);
          const lastItem = filtredItems[filtredItems.length - 1];
          const lastCalendarItemId = getItemId(lastCalendarItem) ?? lastItem?.id;
          filtredItems = filtredItems.filter((it) => Number(it.id) <= Number(lastCalendarItemId));
          const currentYearItem = filtredItems.find((e) => String(e.id).substring(1, 5).includes(currentYear));
          draft.lastCalendar[availableDateRange] = lastCalendarItemId as any;
          draft.items[availableDateRange] = filtredItems as any;

          const applySavedSelection =
            (tab === BulkExportTab.Saved || isFavourite || isSubscribtion) && selection.length > 0 && availableDateRange === dateRange;

          draft.from[availableDateRange] = applySavedSelection ? selection[0] : ((currentYearItem?.id ?? lastCalendarItemId) as any);
          draft.to[availableDateRange] = applySavedSelection ? selection[selection.length - 1] : (lastCalendarItemId as any);
        });
        if (tab && selection) {
          const range = dateRange || `${selection[selection.length - 1]}`[0];
          draft.latestAvailableValue = draft.to[range] === draft.lastCalendar[range];
        }
      });
    }

    case "toggleDateRange": {
      const dateRangeIdentifier = payload.dateRange!;

      return produce(state, (draft) => {
        if (draft.availableDateRanges.length > 1) {
          if (draft.selectedRanges.includes(dateRangeIdentifier)) {
            draft.selectedRanges = draft.selectedRanges.filter((identifier) => identifier !== dateRangeIdentifier);
          } else {
            draft.selectedRanges.push(dateRangeIdentifier);
          }
        }
      });
    }
    case "changeTo": {
      const { dateRange, value } = payload;
      const valueAs = value as any;

      if (valueAs === state.to[dateRange!]) {
        return state;
      }

      return produce(state, (draft) => {
        const fromValue = draft.from[dateRange!]!;
        draft.to[dateRange!] = valueAs;

        if (valueAs < fromValue) {
          draft.from[dateRange!] = valueAs;
        }

        draft.latestAvailableValue = draft.availableDateRanges.every((dateRange) => {
          return draft.lastCalendar[dateRange] === draft.to[dateRange];
        });
        draft.countLastItem[dateRange!] = undefined;
      });
    }

    case "changeFrom": {
      const { dateRange, value } = payload;
      const valueAs = value as any;

      if (valueAs === state.from[dateRange!]) {
        return state;
      }

      return produce(state, (draft) => {
        draft.from[dateRange!] = valueAs;

        const toValue = draft.to[dateRange!]!;

        if (valueAs > toValue) {
          draft.to[dateRange!] = valueAs;
        }
        draft.countLastItem[dateRange!] = undefined;
      });
    }

    case "setLatestAvailableValue": {
      const { value } = payload;
      return produce(state, (draft) => {
        draft.latestAvailableValue = value as boolean;
        if (value) {
          draft.availableDateRanges.forEach((dateRange) => {
            draft.to[dateRange] = draft.lastCalendar[dateRange];
          });
        }
      });
    }

    case "changeCountLastItem": {
      const { dateRange, value } = payload;
      if (value === state.countLastItem[dateRange!]) {
        return state;
      }
      return produce(state, (draft) => {
        draft.latestAvailableValue = true;
        const items = draft.items[dateRange!];
        const lastItemIndex = items.findIndex((it) => Number(it.id) === Number(draft.lastCalendar[dateRange!]));
        const indexFromItem = lastItemIndex - (value as number);
        const fromItem = items[indexFromItem];
        draft.from[dateRange!] = fromItem.id as any;
        draft.to[dateRange!] = draft.lastCalendar[dateRange!];
        draft.countLastItem[dateRange!] = value as number;
      });
    }

    default:
      throw new Error("Bulk calendar control cannot process the command");
  }
}
